import { RgbaColor } from './types';

/**
 * @function generateGaussianRandom
 * @description Generates a random number following a Normal (Gaussian) distribution using the Box-Muller transform.
 * This is a pure function that does not rely on external state.
 * @param {number} [mean=-1] - The desired mean value for the distribution.
 * @param {number} [standardDeviation=1] - The desired standard deviation.
 * @returns {number} A random number following the specified Gaussian distribution.
 */
export function generateGaussianRandom(mean: number = -1, standardDeviation: number = 1): number {
    // [CRITICAL_LOGIC_START]
    // Box-Muller Transform
    const u: number = 1 - Math.random(); // Converting [0,1) to (0,1]
    const v: number = Math.random();
    const z: number = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    
    // Transform to the desired mean and standard deviation:
    return z * standardDeviation + mean;
    // [CRITICAL_LOGIC_END]
}

/**
 * @function convertHexToRgba
 * @description Parses a hexadecimal color string into an RgbaColor object.
 * Supports multiple formats: #RGB, #RGBA, #RRGGBB, #RRGGBBAA.
 * @param {string} hex - The input hex color string (with or without '#' prefix).
 * @returns {RgbaColor} An object containing red, green, blue, and alpha components (0-255).
 */
export function convertHexToRgba(hex: string): RgbaColor {
    // Remove the hash if it's there
    const cleanHex: string = hex.replace(/^#/, '');

    let red: number = 0;
    let green: number = 0;
    let blue: number = 0;
    let alpha: number = 255;

    // [CRITICAL_LOGIC_START]
    if (cleanHex.length === 3 || cleanHex.length === 4) {
        red = parseInt(cleanHex[0] + cleanHex[0], 16);
        green = parseInt(cleanHex[1] + cleanHex[1], 16);
        blue = parseInt(cleanHex[2] + cleanHex[2], 16);
        if (cleanHex.length === 4) {
            alpha = parseInt(cleanHex[3] + cleanHex[3], 16);
        }
    } else if (cleanHex.length === 6 || cleanHex.length === 8) {
        red = parseInt(cleanHex.substring(0, 2), 16);
        green = parseInt(cleanHex.substring(2, 4), 16);
        blue = parseInt(cleanHex.substring(4, 6), 16);
        if (cleanHex.length === 8) {
            alpha = parseInt(cleanHex.substring(6, 8), 16);
        }
    }
    // [CRITICAL_LOGIC_END]

    return { red, green, blue, alpha };
}

/**
 * @function convertHslToHex
 * @description Converts HSL (Hue, Saturation, Lightness) values to a CSS-compatible Hex color string.
 * This is a pure mathematical conversion.
 * @param {number} hue - The hue component (0-359).
 * @param {number} saturation - The saturation component (0-100).
 * @param {number} lightness - The lightness component (0-100).
 * @returns {string} A hex color string in the format '#RRGGBB'.
 */
export function convertHslToHex(hue: number, saturation: number, lightness: number): string {
    const normalizedLightness: number = lightness / 100;
    const a: number = (saturation * Math.min(normalizedLightness, 1 - normalizedLightness)) / 100;
    
    /**
     * @private
     * @function f
     * @description Helper function to calculate RGB components based on position.
     */
    const f = (n: number): string => {
        const k: number = (n + hue / 30) % 12;
        const colorValue: number = normalizedLightness - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * colorValue).toString(16).padStart(2, '0');
    };

    return `#${f(0)}${f(8)}${f(4)}`;
}
